/** Turns an icon recipe into an SVG image source, in the kit's colours (#726). */
import { TOKENS, type Colour } from "./palette";

/** An icon as Hugeicons ships it: a list of [tag, attributes]. */
type Source = ReadonlyArray<readonly [string, Readonly<Record<string, string | number>>]>;

interface Paint {
  fill?: Colour;
  fillOpacity?: number;
  evenodd?: boolean;
  stroke?: Colour;
  strokeOpacity?: number;
  /** Stroke width in grid units; 1 px unless given. */
  width?: number;
  cap?: "round" | "square";
  join?: "round";
  dash?: string;
}

/** One painted shape: an element of the source icon (`use`, by index) or a shape of our own. */
type Layer = Paint & ({ use: number } | { d: string } | { circle: [cx: number, cy: number, r: number] } | { rect: [x: number, y: number, w: number, h: number, rx: number] });

export interface Recipe {
  /** Size in CSS pixels. */
  w: number;
  h: number;
  /** The drawing grid; 24 × 24 unless given. */
  grid?: [number, number];
  /** Fill the box even when its proportions differ (rules that stretch). */
  stretch?: boolean;
  from?: Source;
  layers: Layer[];
}

const attr = (name: string, value: string | number | undefined) => (value === undefined ? "" : ` ${name}="${value}"`);

const GEOMETRY = ["cx", "cy", "r", "x", "y", "width", "height", "rx"] as const;

/** Shapes stay on their drawing grid, exactly as their source writes them; the viewBox scales them to size. */
function shape(layer: Layer, from: Source | undefined): string {
  if ("d" in layer) return `<path d="${layer.d}"`;
  if ("circle" in layer) return `<circle${GEOMETRY.slice(0, 3).map((name, i) => attr(name, layer.circle[i])).join("")}`;
  if ("rect" in layer) return `<rect${GEOMETRY.slice(3).map((name, i) => attr(name, layer.rect[i])).join("")}`;
  const element = from?.[layer.use];
  if (!element) throw new Error(`Icon recipe uses element ${layer.use}, which its source does not have.`);
  const [tag, source] = element;
  return `<${tag}${["d", ...GEOMETRY].map((name) => attr(name, source[name])).join("")}`;
}

/** A colour as the image writes it: its token's value. */
const resolve = (colour: Colour | undefined) => (colour === undefined || colour === "white" || colour === "black" ? colour : TOKENS[colour]);

export function paint({ w, h, grid = [24, 24], stretch, from, layers }: Recipe): string {
  const body = layers
    .map(
      (layer) =>
        shape(layer, from) +
        attr("fill", resolve(layer.fill)) +
        attr("fill-opacity", layer.fillOpacity) +
        attr("fill-rule", layer.evenodd ? "evenodd" : undefined) +
        attr("stroke", resolve(layer.stroke)) +
        attr("stroke-opacity", layer.strokeOpacity) +
        attr("stroke-width", layer.stroke && (layer.width ?? grid[0] / w)) +
        attr("stroke-linecap", layer.cap) +
        attr("stroke-linejoin", layer.join) +
        attr("stroke-dasharray", layer.dash) +
        "/>",
    )
    .join("");
  const svg = `<svg width="${w}" height="${h}" viewBox="0 0 ${grid[0]} ${grid[1]}" fill="none"${attr("preserveAspectRatio", stretch ? "none" : undefined)} xmlns="http://www.w3.org/2000/svg">${body}</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
